import { useUpdateIncidentFields } from '@hakimo-ui/hakimo/data-access';
import { DataVersion, UnifiedMonitoringProduct } from '@hakimo-ui/hakimo/types';
import { FeatureFlag, toast, useFeatureFlag } from '@hakimo-ui/hakimo/util';
import { EditActions, EditButton, Textarea } from '@hakimo-ui/shared/ui-base';
import { useState } from 'react';
import { BOX_CLASS } from './DispatchTimelineEntryViewCompact';

interface Props {
  alarmId: string;
  description: string;
  product: UnifiedMonitoringProduct;
  dataVersion?: DataVersion;
  onSaved: (description: string) => void;
}

export function EscalationDescriptionField(props: Props) {
  const { alarmId, description, product, dataVersion, onSaved } = props;

  const isSOPV2Enabled = useFeatureFlag(FeatureFlag.SOP_WORKFLOW_V2);

  const [draft, setDraft] = useState(description);
  const [editing, setEditing] = useState(false);

  const { mutate, isLoading } = useUpdateIncidentFields(
    product === UnifiedMonitoringProduct.SCAN ? 'scan' : 'remote-guarding',
    () => {
      setEditing(false);
      onSaved(draft.trim());
      toast('Escalation description updated');
    },
    () =>
      toast('Could not update the escalation description. Please retry.', {
        type: 'error',
      }),
    dataVersion
  );

  const startEdit = () => {
    setDraft(description);
    setEditing(true);
  };

  const save = () =>
    mutate({
      alarm_id: alarmId,
      fields: { reported_description: draft.trim() },
    });

  if (!isSOPV2Enabled) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <div className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-semibold tracking-wide">
          Escalation Description
        </div>
        {!editing && (
          <EditButton label="Escalation Description" onClick={startEdit} />
        )}
      </div>

      {editing ? (
        <div>
          <Textarea
            value={draft}
            onChange={setDraft}
            rows={3}
            placeholder="Describe the incident"
          />
          <EditActions
            saving={isLoading}
            onSave={save}
            onCancel={() => setEditing(false)}
          />
        </div>
      ) : (
        <div
          className={`${BOX_CLASS} text-onlight-text-1 dark:text-ondark-text-1 whitespace-pre-wrap text-sm`}
        >
          {description || '-'}
        </div>
      )}
    </div>
  );
}

export default EscalationDescriptionField;
